import React from 'react';
import { Button, ButtonIcon, ButtonText } from '@/components/gluestack/button';
import { useTheme, ThemeMode } from '@/components/providers/ThemeProvider';
import { Sun03Icon, Moon02Icon } from '@hugeicons-pro/core-solid-rounded';
import { ChevronDownIcon } from '@/components/gluestack/icon';
import { HStack } from '@/components/gluestack/hstack';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { MenuSelectButton } from '@/components/ui/MenuSelectButton';

type Size = 'xs' | 'sm' | 'md' | 'lg';

type LabelMap = Record<ThemeMode, string>;

interface ThemeSwitcherProps {
  size?: Size;
  labelMap?: LabelMap;
  neutral?: boolean;
  /** If true, show only an icon in the trigger (no text label). */
  iconOnly?: boolean;
}

export default function ThemeSwitcher({ size = 'md', labelMap, neutral, iconOnly = false }: ThemeSwitcherProps) {
  const { themeMode, effectiveColorScheme, setThemeMode } = useTheme();

  const options: ThemeMode[] = ['auto', 'light', 'dark'];
  const resolvedScheme = themeMode === 'auto' ? effectiveColorScheme : themeMode;
  const icon = resolvedScheme === 'dark' ? Moon02Icon : Sun03Icon;
  const defaultLabels: LabelMap = { auto: 'Auto', light: 'Light', dark: 'Dark' } as const;
  const labels = labelMap ?? defaultLabels;
  const label = labels[themeMode];

  return (
    <HStack className="items-center gap-2">
      <MenuSelectButton
        selectedKey={themeMode}
        options={options.map((mode) => ({ key: mode, label: labels[mode] }))}
        onSelect={(key) => setThemeMode(key as ThemeMode)}
        placement="bottom right"
        offset={8}
        trigger={({ ...triggerProps }) => (
          <Button
            {...triggerProps}
            size={size}
            className={iconOnly ? 'gap-0 px-2' : 'gap-2'}
            action={neutral ? 'neutral' : undefined}>
            {iconOnly ? (
              <HugeIconsWrapper
                icon={icon}
                size={20}
                className={neutral ? 'color-typography-500' : 'color-white'}
              />
            ) : (
              <>
                <HugeIconsWrapper
                  icon={icon}
                  size={20}
                  className={neutral ? 'color-typography-500' : 'color-white'}
                />
                <ButtonText>{label}</ButtonText>
                <ButtonIcon
                  as={ChevronDownIcon}
                  size="sm"
                  className={neutral ? 'color-typography-500' : 'color-white'}
                />
              </>
            )}
          </Button>
        )}
      />
    </HStack>
  );
}


